import { useEffect, useRef, useState } from "react";

// Lazy-load leaflet to avoid TDZ issues with module-level execution
let L = null;
let MapContainer = null;
let TileLayer = null;
let Polyline = null;
let Marker = null;
let useMap = null;
let leafletLoaded = false;

const loadLeaflet = async () => {
  if (leafletLoaded) return;
  const leaflet = await import("leaflet");
  const rl = await import("react-leaflet");
  await import("leaflet/dist/leaflet.css");
  L = leaflet.default || leaflet;
  MapContainer = rl.MapContainer;
  TileLayer = rl.TileLayer;
  Polyline = rl.Polyline;
  Marker = rl.Marker;
  useMap = rl.useMap;

  // Fix leaflet default icon issue
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  });
  leafletLoaded = true;
};

// Inner map component — only renders after leaflet is loaded
const InnerMap = ({ gpsPoints, isLive, height, routeColor, tileUrl, attribution }) => {
  const positions = gpsPoints
    .filter(p => p.latitude && p.longitude)
    .map(p => [p.latitude, p.longitude]);

  if (positions.length < 2) return null;

  const center = positions[Math.floor(positions.length / 2)];
  const tile = tileUrl || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const attr = attribution || "\u00a9 OpenStreetMap contributors";

  const startIcon = new L.DivIcon({
    className: "run-map-start-marker",
    html: '<div style="width:12px;height:12px;border-radius:50%;background:#4ADE80;border:2px solid #fff;box-shadow:0 0 6px rgba(74,222,128,0.6)"></div>',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });

  const currentIcon = new L.DivIcon({
    className: "run-map-current-marker",
    html: '<div style="width:14px;height:14px;border-radius:50%;background:#00D4FF;border:2px solid #fff;box-shadow:0 0 10px rgba(0,212,255,0.7);animation:pulse-marker 1.5s ease-in-out infinite"></div>',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

  return (
    <div className="run-map-container" style={{ height, borderRadius: 12, overflow: "hidden" }}>
      <MapContainer
        center={center}
        zoom={15}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer url={tile} attribution={attr} />
        <Polyline positions={positions} pathOptions={{ color: routeColor || "#00D4FF", weight: 3, opacity: 0.9 }} />
        <Marker position={positions[0]} icon={startIcon} />
        {positions.length > 1 && (
          <Marker position={positions[positions.length - 1]} icon={isLive ? currentIcon : startIcon} />
        )}
        <FitBoundsInner positions={positions} isLive={isLive} />
      </MapContainer>
    </div>
  );
};

const FitBoundsInner = ({ positions, isLive }) => {
  const map = useMap();
  const prevLen = useRef(0);

  useEffect(() => {
    if (!positions || positions.length === 0) return;
    if (isLive && positions.length > prevLen.current) {
      const last = positions[positions.length - 1];
      map.panTo(last, { animate: true });
    } else if (!isLive && positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [30, 30] });
    }
    prevLen.current = positions.length;
  }, [positions, isLive, map]);

  return null;
};

const RunMap = ({ gpsPoints = [], isLive = false, height = 200, routeColor = "#00D4FF", tileUrl, attribution }) => {
  const [ready, setReady] = useState(leafletLoaded);

  useEffect(() => {
    if (!leafletLoaded) {
      loadLeaflet().then(() => setReady(true));
    }
  }, []);

  if (!gpsPoints || gpsPoints.length < 2) {
    return (
      <div className="run-map-placeholder" style={{
        height, borderRadius: 12, background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--text-muted)", fontSize: "0.8rem"
      }}>
        Oczekiwanie na dane GPS...
      </div>
    );
  }

  if (!ready) {
    return (
      <div style={{
        height, borderRadius: 12, background: "rgba(255,255,255,0.03)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--text-muted)", fontSize: "0.8rem"
      }}>
        Ladowanie mapy...
      </div>
    );
  }

  return <InnerMap gpsPoints={gpsPoints} isLive={isLive} height={height} routeColor={routeColor} tileUrl={tileUrl} attribution={attribution} />;
};

// Mini map — lightweight SVG polyline (no tile requests, no leaflet needed)
const MiniRunMap = ({ gpsPoints = [], size = 56, color = "#00D4FF" }) => {
  if (!gpsPoints || gpsPoints.length < 2) return null;

  const points = gpsPoints.filter(p => p.latitude && p.longitude);
  if (points.length < 2) return null;

  const lats = points.map(p => p.latitude);
  const lngs = points.map(p => p.longitude);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const latRange = maxLat - minLat || 0.001;
  const lngRange = maxLng - minLng || 0.001;
  const pad = 4;
  const w = size - pad * 2;
  const h = size - pad * 2;

  const svgPoints = points.map(p => {
    const x = pad + ((p.longitude - minLng) / lngRange) * w;
    const y = pad + ((maxLat - p.latitude) / latRange) * h;
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mini-run-map">
      <rect width={size} height={size} rx={8} fill="rgba(255,255,255,0.03)" />
      <polyline points={svgPoints} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" opacity={0.8} />
      <circle cx={svgPoints.split(" ")[0].split(",")[0]} cy={svgPoints.split(" ")[0].split(",")[1]} r={3} fill="#4ADE80" />
      <circle
        cx={svgPoints.split(" ").slice(-1)[0].split(",")[0]}
        cy={svgPoints.split(" ").slice(-1)[0].split(",")[1]}
        r={3} fill={color}
      />
    </svg>
  );
};

export { RunMap, MiniRunMap };
export default RunMap;
